import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { AlertTriangle, Radio } from "lucide-react";
import { DEFCON_COLORS, DEFCON_ORDER } from "@/lib/defcon";
import DefconHistory from "@/components/DefconHistory";

// Public live DEFCON meter, rendered on the Crashout show page.
export default function FreshyDefcon({ showId }) {
  const [d, setD] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/defcon");
      setD(data);
    } catch {}
  };
  useEffect(() => {
    load();
    const t = setInterval(load, 30000); // keep it live-ish
    return () => clearInterval(t);
  }, []);

  // Only show on the show this DEFCON is bound to.
  if (!d || !d.show_id || d.show_id !== showId) return null;

  const level = d.current_level || 5;
  const color = DEFCON_COLORS[level] || "#FF3B30";
  const meta = (d.levels && d.levels[String(level)]) || {};

  return (
    <div
      className="relative overflow-hidden border p-8 mb-16"
      style={{ borderColor: `${color}66`, background: `linear-gradient(135deg, ${color}14 0%, transparent 60%)` }}
      data-testid="defcon-meter"
    >
      <div
        className="pointer-events-none absolute -top-24 -right-16 w-80 h-80 rounded-full blur-[120px] opacity-40"
        style={{ background: color }}
      />
      <div className="relative">
        <div className="label-eyebrow mb-4 flex items-center gap-2" style={{ color }}>
          <Radio size={13} /> {d.name || "Freshy Defcon"}
        </div>

        <div className="flex flex-col lg:flex-row lg:items-end gap-8">
          {/* Big readout */}
          <div className="shrink-0">
            <div className="font-mono-ui text-xs uppercase tracking-[0.25em] text-[var(--ff-text-dim)] mb-1">Current Status</div>
            <div className="flex items-end gap-4">
              <div className="font-display leading-none" style={{ fontSize: "5.5rem", color }} data-testid="defcon-current-level">
                {level}
              </div>
              <div className="pb-3">
                <div className="font-display text-3xl leading-tight" data-testid="defcon-current-label">{meta.label}</div>
                {level <= 2 && (
                  <div className="flex items-center gap-1 mt-1 font-mono-ui text-xs uppercase tracking-[0.15em]" style={{ color }}>
                    <AlertTriangle size={12} /> High alert
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Level ladder */}
          <div className="flex-1 w-full">
            <div className="space-y-1.5" data-testid="defcon-ladder">
              {DEFCON_ORDER.map((lvl) => {
                const active = lvl === level;
                const c = DEFCON_COLORS[lvl];
                const lm = (d.levels && d.levels[String(lvl)]) || {};
                return (
                  <div
                    key={lvl}
                    className="flex items-center gap-3 px-3 py-2 border transition-all"
                    style={{
                      borderColor: active ? c : "var(--ff-border)",
                      background: active ? `${c}22` : "transparent",
                      opacity: active ? 1 : 0.55,
                    }}
                    data-testid={`defcon-rung-${lvl}`}
                  >
                    <span className="font-display text-xl w-7 text-center" style={{ color: active ? c : "var(--ff-text-dim)" }}>{lvl}</span>
                    <span className="w-2 h-2 rounded-full" style={{ background: c }} />
                    <span className={`font-mono-ui text-xs uppercase tracking-[0.12em] ${active ? "text-white" : "text-[var(--ff-text-dim)]"}`}>
                      {lm.label}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {meta.description && (
          <p className="mt-6 text-[var(--ff-text-dim)] text-sm leading-relaxed max-w-3xl" data-testid="defcon-current-desc">
            {meta.description}
          </p>
        )}
        {d.description && (
          <p className="mt-3 font-mono-ui text-[0.65rem] uppercase tracking-[0.15em] text-[var(--ff-text-dim)]/70">
            {d.description}
          </p>
        )}

        <DefconHistory className="mt-8 pt-6 border-t border-[var(--ff-border)]/60" />
      </div>
    </div>
  );
}
